import { ApiProblem } from '@cdn/api';
import type { Identity, SignInRequest } from '../port';

interface Specs {
	url: string;
	/** The public anonymous key. A service-role key must never reach a browser. */
	key: string;
	/** Absolute address of the return route, `<origin>/signin/return` */
	returning: string;
	storage?: Storage;
}

type Client = import('@supabase/supabase-js').SupabaseClient;

/**
 * UNVALIDATED: no Supabase project exists for Beyond yet, so this adapter was never run against a service.
 *
 * GitHub, Google and email/password through Supabase Auth. It uses the PKCE flow, so the provider returns a
 * one-time code and no token ever appears in a URL, and it keeps the session in `sessionStorage`, never in a
 * cookie. The CDN API must accept the Supabase access token through its own authority adapter.
 */
export class SupabaseIdentity implements Identity {
	readonly name = 'supabase';
	readonly validated = false;
	readonly methods = ['github', 'google', 'password'] as const;

	#specs: Specs;
	#client: Promise<Client> | undefined;

	constructor(specs: Specs) {
		this.#specs = specs;
	}

	// The library is loaded only when this adapter is selected
	#connect(): Promise<Client> {
		const { url, key, storage } = this.#specs;
		if (!url || !key) throw new ApiProblem('UNAVAILABLE', 'The Supabase identity adapter is not configured', 0);
		this.#client ??= import('@supabase/supabase-js').then(({ createClient }) =>
			createClient(url, key, { auth: { flowType: 'pkce', persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, storage } })
		);
		return this.#client;
	}

	async restore() {
		const client = await this.#connect();
		const { data } = await client.auth.getSession();
		return data.session?.access_token;
	}

	async signin(request: SignInRequest) {
		const client = await this.#connect();
		if (request.method === 'github' || request.method === 'google') {
			const { error } = await client.auth.signInWithOAuth({ provider: request.method, options: { redirectTo: this.#specs.returning } });
			if (error) throw new ApiProblem('UNAUTHENTICATED', error.message, error.status ?? 0);
			return undefined;
		}
		if (request.method !== 'password') throw new ApiProblem('UNSUPPORTED_INPUT', `The Supabase adapter has no "${request.method}" sign-in`, 0);

		const credentials = { email: request.email, password: request.password };
		const { data, error } = request.create
			? await client.auth.signUp({ ...credentials, options: { emailRedirectTo: this.#specs.returning } })
			: await client.auth.signInWithPassword(credentials);
		if (error) throw new ApiProblem('UNAUTHENTICATED', error.message, error.status ?? 0);
		return data.session?.access_token;
	}

	async signout() {
		const client = await this.#connect();
		await client.auth.signOut();
	}

	rotated(listener: (token: string | undefined) => void) {
		let release = () => {};
		let ended = false;
		void this.#connect().then(client => {
			const { data } = client.auth.onAuthStateChange((_, session) => listener(session?.access_token));
			release = () => data.subscription.unsubscribe();
			ended && release();
		});
		return () => {
			ended = true;
			release();
		};
	}
}
